import React from 'react';
import clsx from 'clsx';
import { Settings } from '@mui/icons-material';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Divider, Tooltip, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './SideBar.scss';
import TaskSideBar from '../../../components/SideBar/TaskSideBar/TaskSideBar';
import { ChatSideBar, CalendarsList, GeneralCTA, TodoList } from '../../../components';
import {
  useCalendarContext,
  settingsSidebarStateAtom,
  sidebarOpenAtom,
  syncErrorRootEmailAtom,
  useAssistantChat,
  isAllPlanPanelOpenAtom,
} from '../../../data-access';

import { useHandleCalSyncError } from '../../../hooks';
import { Path } from '../../../routing';
import { ReactComponent as NewChatIcon } from '../../../assets/chat/new-chat.svg';
import { SIDEBAR_MENU } from './constants';
import { sidebarVariants } from './Sidebar.variants';

interface SideBarProps {
  className?: string;
}

const SideBar: React.FC<SideBarProps> = ({ className }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const setSettingsSidebarState = useSetRecoilState(settingsSidebarStateAtom);
  const { openAssistantChat, setOpenAssistantChat, clearMessages, messages, isSchedulerMode } =
    useAssistantChat();
  const { calendarsMeta, visibleCalendarIds, isFetchingCalendars, handleSelectCalendar } =
    useCalendarContext();
  const [open, setOpen] = useRecoilState(sidebarOpenAtom);
  const syncErrorRootEmail = useRecoilValue(syncErrorRootEmailAtom);
  const isAllPlanPanelOpen = useRecoilValue(isAllPlanPanelOpenAtom);

  React.useEffect(() => {
    localStorage.setItem('sidebarOpen', open ? 'true' : 'false');
  }, [open]);
  const isTasksPage = pathname === Path.TASK;
  const [activeTab, setActiveTab] = React.useState<'chat' | 'settings'>(() =>
    isTasksPage && !openAssistantChat ? 'settings' : 'chat'
  );

  const { handleConnect, handleDisconnect, isConnectingCalendars, isDisconnectingCalendars } =
    useHandleCalSyncError({ syncErrorRootEmail });

  React.useEffect(() => {
    if (!isTasksPage) {
      setActiveTab('chat');
      setOpenAssistantChat(true);
    }
  }, [isTasksPage, setOpenAssistantChat]);

  React.useEffect(() => {
    if (isTasksPage) {
      setActiveTab(openAssistantChat ? 'chat' : 'settings');
    }
  }, [isTasksPage, openAssistantChat]);

  // Plan My Day results live in the chat tab — switch there when they open.
  React.useEffect(() => {
    if (isAllPlanPanelOpen) {
      setActiveTab('chat');
      setOpenAssistantChat(true);
    }
  }, [isAllPlanPanelOpen, setOpenAssistantChat]);

  const handleTabChange = (tab: 'chat' | 'settings') => {
    setActiveTab(tab);
    setOpenAssistantChat(tab === 'chat');
  };

  const openAiAssistantSettings = () => {
    setSettingsSidebarState({
      selectedParentKey: 'aiAssistant',
      expandedParentKeys: [],
    });
    navigate(`${Path.HOME}/settings`);
    setOpenAssistantChat(false);
  };

  const newChatLabel = t('task.common.newChat').replace(/^\+\s*/, '');
  const isNewChat =
    !isSchedulerMode &&
    (messages.length === 0 ||
      (messages.length === 1 && messages[0]?.role === 'assistant' && !!messages[0]?.start));

  const sidebarMainContent = (
    <div className="sidebar__inner">
      <div className="sidebar__header">
        <div className="sidebar__tab-bar" role="tablist" aria-label={t('task.sidebar.tabs')}>
          <button
            type="button"
            role="tab"
            data-testid="ai-chat-tab"
            aria-selected={activeTab === 'chat'}
            className={clsx('sidebar__tab', activeTab === 'chat' && 'sidebar__tab--active')}
            onClick={() => handleTabChange('chat')}
          >
            <span className="sidebar__tab-chat-content">
              <span className="sidebar__tab-chat-label">{t('task.sidebar.chat')}</span>
              <span
                role="button"
                tabIndex={0}
                aria-label={t('task.sidebar.openAiAssistantSettings')}
                className="sidebar__tab-chat-label__settings-button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openAiAssistantSettings();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    openAiAssistantSettings();
                  }
                }}
              >
                <Settings className="sidebar__tab-chat-label__settings-button__icon" />
              </span>
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'settings'}
            className={clsx('sidebar__tab', activeTab === 'settings' && 'sidebar__tab--active')}
            onClick={() => handleTabChange('settings')}
          >
            {isTasksPage ? (
              t('task.filters.filters')
            ) : (
              <span className="sidebar__tab-calendar-label">{t('task.sidebar.integrations')}</span>
            )}
          </button>
        </div>
        <div className="sidebar__header__actions">
          {activeTab === 'chat' && !isNewChat && (
            <Tooltip title={newChatLabel} placement="bottom">
              <button
                type="button"
                className="sidebar__header__new-chat"
                aria-label={newChatLabel}
                onClick={clearMessages}
              >
                <NewChatIcon />
              </button>
            </Tooltip>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="sidebar__header__menu-icon"
            aria-label={t('task.sidebar.collapseSidebar')}
          >
            <SIDEBAR_MENU.close title={t('task.sidebar.closeMenu')} />
          </button>
        </div>
      </div>
      <div className="sidebar__content">
        <AnimatePresence mode="wait">
          {activeTab === 'chat' ? (
            <motion.div
              key="chat-tab"
              className="sidebar__panel"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <ChatSideBar />
            </motion.div>
          ) : (
            <motion.div
              key="settings-tab"
              className="sidebar__panel"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {isTasksPage ? (
                <TaskSideBar />
              ) : (
                <>
                  <Typography variant="body1" className="sidebar__panel-subtitle">
                    {t('task.sidebar.integrations')}
                  </Typography>
                  <CalendarsList
                    calendarsMeta={calendarsMeta}
                    visibleCalendarIds={visibleCalendarIds}
                    isFetchingCalendars={isFetchingCalendars}
                    syncErrorRootEmail={syncErrorRootEmail}
                    onSelectCalendar={handleSelectCalendar}
                    onDisconnectCalendar={handleDisconnect}
                    onConnectCalendar={handleConnect}
                    isConnectingCalendars={isConnectingCalendars}
                    isDisconnectingCalendars={isDisconnectingCalendars}
                    className="sidebar__calendars"
                  />
                  <Divider className="sidebar__panel-divider" />
                  <Typography variant="body1" className="sidebar__panel-subtitle">
                    {t('task.todoIntegration.todos')}
                  </Typography>
                  <TodoList className="sidebar__todos" />
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {!isTasksPage && activeTab === 'settings' && <GeneralCTA className="sidebar__general-cta" />}
    </div>
  );

  return (
    <motion.div
      variants={sidebarVariants}
      initial={false}
      animate={open ? 'open' : 'closed'}
      className={clsx('sidebar', activeTab === 'chat' && 'sidebar--chat-active', className)}
      style={{ pointerEvents: open ? 'auto' : 'none' }}
      aria-hidden={!open}
    >
      {sidebarMainContent}
    </motion.div>
  );
};

export default SideBar;
